import { CommonModule } from "@angular/common";
import { Component, computed, input } from "@angular/core";
import { Answer } from "@domain/Answer";
import { AudioExercise, Exercise } from "@domain/Exercise";
import { normalizeString } from "@helpers/string.helper";

@Component({
  selector: "app-feedback",
  imports: [CommonModule],
  template: `
    @if (answer().isCorrect()) {
      <div class="feedback feedback--success" data-testid="positive-feedback">
        <p class="feedback__title title-h5">Bravo!</p>
        <p class="feedback__correct-sentence body-2" [innerHTML]="exercise().positiveFeedback"></p>
        @if (shouldShowCapitalizationWarning()) {
          <p class="feedback__capitalization-wraning body-2">Tu as bien écrit le bon pronom, mais fais attention aux majuscules.</p>
        }
        <img class="feedback__flo" src="assets/flo/flo-success.svg" alt="Flo positive feedback" />
      </div>
    } @else if (answer().isIncorrect()) {
      <div class="feedback feedback--warning" data-testid="constructive-feedback">
        <p class="feedback__title title-h5">Oups!</p>
        <p class="feedback__correct-sentence body-2" [innerHTML]="exercise().constructiveFeedback"></p>
        <img class="feedback__flo" src="assets/flo/flo-unsuccess.svg" alt="Flo constructive feedback" />
      </div>
    }
  `,
  styles: `
    @use "responsive" as *;

    :host {
      display: block;
      width: 100%;
      margin-top: auto;
    }

    .feedback {
      position: relative;
      z-index: 5000;
      width: auto;
      min-height: 134px;
      margin: 0 -24px -24px;
      padding: 24px;
      border-radius: 0px 0px 24px 24px;
      overflow: hidden;

      @include screenWidth("from-tablet") {
        margin: 0 -48px -32px;
        padding: 24px 48px;
      }
    }

    .feedback--success {
      background-color: #cdf2e8;

      .feedback__title,
      .feedback__correct-sentence {
        width: 100%;

        @include screenWidth("from-desktop") {
          width: calc(100% - 256px);
        }
      }

      .feedback__flo {
        position: absolute;
        bottom: 0;
        right: 24px;
        display: block;
        width: 215px;
        z-index: 5500;

        @include onDesktopOnly();
      }
    }

    .feedback--warning {
      background-color: #fccdb0;

      .feedback__title,
      .feedback__correct-sentence {
        width: 100%;

        @include screenWidth("from-desktop") {
          width: calc(100% - 256px);
        }
      }

      .feedback__flo {
        position: absolute;
        top: 0;
        right: -20px;
        display: block;
        width: 259px;
        z-index: 5500;

        @include onDesktopOnly();
      }
    }

    .feedback__title {
      margin-bottom: 8px;
    }

    .feedback__capitalization-wraning {
      margin-top: 8px;
    }
  `,
})
export class Feedback {
  exercise = input.required<Exercise>();
  answer = input.required<Answer>();

  shouldShowCapitalizationWarning = computed<boolean>(() => {
    const exercise = this.exercise();
    if (!(exercise instanceof AudioExercise)) return false;

    const answer = this.answer();
    if (!answer.isCorrect()) return false;

    const normalizedCorrectAnswer = normalizeString(exercise.correctAnswer).trim();
    const normalizedAnswerValue = normalizeString(answer.value).trim();

    return normalizedCorrectAnswer !== normalizedAnswerValue;
  });
}
